/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { alCoerceNumber, alCoerceString, alToBoolean, alToInt32, alToInteger, alToNumber, alToString } from '../runtime';
import { getAVM1Object, wrapAVM1NativeClass, toTwipFloor, toTwipRound, away2avmDepth } from './AVM1Utils';
import { AVM1TextFormat } from './AVM1TextFormat';
import { notImplemented } from '@awayfl/swf-loader';
import { KeyboardEvent, TextFormat, TextfieldEvent } from '@awayjs/scene';
import { AVM1Key } from './AVM1Key';
import { AVM1SymbolBase } from './AVM1SymbolBase';
import { EventsListForMC } from './AVM1EventHandler';
import { AVM1Globals } from './AVM1Globals';
import { alCallProperty, } from '../runtime';
var AVM1TextField = /** @class */ (function (_super) {
    __extends(AVM1TextField, _super);
    function AVM1TextField() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._prevTextVarContent = '';
        _this._textVarHolder = null;
        _this._textVarPropName = '';
        return _this;
    }
    AVM1TextField.createAVM1Class = function (context) {
        return wrapAVM1NativeClass(context, true, AVM1TextField, [], ['_alpha#', 'addListener', 'antiAliasType#', 'autoSize#', 'background#', 'backgroundColor#',
            'border#', 'borderColor#', 'bottomScroll#', 'condenseWhite#', 'embedFonts#',
            'filters#', 'getAwayJSID', 'getNewTextFormat', 'getTextFormat', 'gridFitType#', 'getDepth',
            '_height#', '_highquality#', 'hscroll#', 'html#', 'htmlText#', 'length#',
            'maxChars#', 'maxhscroll#', 'maxscroll#', 'multiline#',
            '_name#', '_parent#', 'password#', '_quality#', '_rotation#',
            'replaceSel', 'removeMovieClip',
            'removeTextField', 'restrict#', 'scroll#', 'selectable#', 'setNewTextFormat', 'setTextFormat',
            '_soundbuftime#', 'tabEnabled#', 'tabIndex#', '_target#', 'toString',
            'text#', 'textColor#', 'textHeight#', 'textWidth#', 'type#',
            '_url#', 'variable#', '_visible#', '_width#', 'wordWrap#',
            '_x#', '_xmouse#', '_xscale#', '_y#', '_ymouse#', '_yscale#']);
    };
    AVM1TextField.prototype.dispatchKeyEvent = function (keyCode, isShift, isCTRL, isAlt) {
        // this is called from the adaptee whenever a text-input occurs
        if (!this.context.globals.Stage
            || !this.context.globals.Stage.avmStage) {
            return;
        }
        if (this.context.globals.Stage.avmStage.mouseManager.useSoftkeyboard) {
            //console.log("dispatch keyEvent")
            var staticState = this.context.getStaticState(AVM1Key);
            staticState._lastKeyCode = keyCode;
            staticState._keyStates[keyCode] = 1;
            alCallProperty(AVM1Globals.instance.Key, 'broadcastMessage', ['onKeyDown']);
            var newEvent = new KeyboardEvent(KeyboardEvent.KEYDOWN, '', keyCode);
            newEvent.isShift = isShift;
            newEvent.isCTRL = isCTRL;
            newEvent.isAlt = isAlt;
            this.context.globals.Stage.avmStage.dispatchEvent(newEvent);
            delete staticState._keyStates[keyCode];
            alCallProperty(AVM1Globals.instance.Key, 'broadcastMessage', ['onKeyUp']);
            newEvent = new KeyboardEvent(KeyboardEvent.KEYUP, '', keyCode);
            newEvent.isShift = isShift;
            newEvent.isCTRL = isCTRL;
            newEvent.isAlt = isAlt;
            this.context.globals.Stage.avmStage.dispatchEvent(newEvent);
        }
    };
    AVM1TextField.syncAllTextfields = function () {
        for (var key in AVM1TextField.allTextfields) {
            if (AVM1TextField.allTextfields[key]) {
                AVM1TextField.allTextfields[key].syncTextFieldValue();
            }
            else {
                delete AVM1TextField.allTextfields[key];
            }
        }
    };
    AVM1TextField.syncQueedTextfields = function () {
        if (AVM1TextField.textFieldVars.length > 0) {
            var len = AVM1TextField.textFieldVars.length;
            for (var i = 0; i < len; i++) {
                AVM1TextField.textFieldVars[i].syncTextFieldValue();
            }
            AVM1TextField.textFieldVars.length = 0;
        }
    };
    AVM1TextField.prototype.getAwayJSID = function () {
        return this.adaptee.id;
    };
    AVM1TextField.prototype.selectTextField = function (fromMouseDown) {
        if (fromMouseDown === void 0) { fromMouseDown = false; }
        // this is called from the adaptee whenever it is selected in MouseManager
        if (AVM1Globals.softKeyboardManager) {
            if (this.adaptee.name == 'dummy_txt' || this.adaptee.name.indexOf('temp') == 0)
                fromMouseDown = true;
            AVM1Globals.softKeyboardManager.openKeyboard(this.adaptee, fromMouseDown);
        }
    };
    AVM1TextField.prototype.removeMovieClip = function () {
        if (this.adaptee.isAVMScene) {
            return; // let's not remove root symbol
        }
        if (this.adaptee.parent && away2avmDepth(this.adaptee._avmDepthID) >= -1) {
            var avm1parent = this.adaptee.parent.adapter;
            avm1parent.removeChildAtDepth(this.adaptee._avmDepthID);
        }
    };
    AVM1TextField.prototype.alPut = function (p, v) {
        _super.prototype.alPut.call(this, p, v);
    };
    AVM1TextField.prototype.initAdapter = function () {
        var _this = this;
        if (this.adaptee._symbol) {
            //console.log("do init", this.adaptee._symbol.variableName, this);
            this.setVariable(this.adaptee._symbol.variableName || '');
        }
        this.adaptee.addEventListener(TextfieldEvent.CHANGED, function () {
            // make sure text-var is synced before onChanged is called:
            _this.updateVarFromText();
            _this.syncTextFieldValue();
        });
        //this._initEventsHandlers();
    };
    AVM1TextField.prototype.initAVM1SymbolInstance = function (context, awayObject) {
        _super.prototype.initAVM1SymbolInstance.call(this, context, awayObject);
        //this.dynamicallyCreated=false;
        this.adaptee = awayObject;
        this._initEventsHandlers();
        this._variable = '';
        this._exitFrameHandler = null;
        this.adaptee = awayObject;
    };
    AVM1TextField.prototype.clone = function () {
        return getAVM1Object(this.adaptee.clone(), this._avm1Context);
    };
    AVM1TextField.prototype.getAntiAliasType = function () {
        notImplemented('AVM1Textfield.getAntiAliasType');
        return ''; // this.adaptee.antiAliasType;
    };
    AVM1TextField.prototype.setAntiAliasType = function (value) {
        notImplemented('AVM1Textfield.getAntiAliasType');
        //value = alCoerceString(this.context, value);
        //this.adaptee.antiAliasType = value;
    };
    AVM1TextField.prototype.get_width = function () {
        this.syncTextFieldValue();
        return toTwipRound(this.adaptee.width);
    };
    AVM1TextField.prototype.get_height = function () {
        this.syncTextFieldValue();
        return toTwipRound(this.adaptee.height);
    };
    AVM1TextField.prototype.set_width = function (value) {
        value = toTwipRound(alToNumber(this.context, value));
        this._blockedByScript = true;
        if (isNaN(value))
            return;
        this.adaptee.width = value;
    };
    AVM1TextField.prototype.set_height = function (value) {
        value = toTwipRound(alToNumber(this.context, value));
        this._blockedByScript = true;
        if (isNaN(value))
            return;
        this.adaptee.height = value;
    };
    AVM1TextField.prototype.get_x = function () {
        this.syncTextFieldValue();
        return toTwipFloor(this.adaptee.x + (this.adaptee.scaleX * this.adaptee.textOffsetX));
    };
    AVM1TextField.prototype.get_y = function () {
        this.syncTextFieldValue();
        return toTwipFloor(this.adaptee.y + (this.adaptee.scaleY * this.adaptee.textOffsetY));
    };
    AVM1TextField.prototype.set_x = function (value) {
        this.syncTextFieldValue();
        this.adaptee.x = toTwipFloor(value - (this.adaptee.scaleX * this.adaptee.textOffsetX));
    };
    AVM1TextField.prototype.set_y = function (value) {
        this.syncTextFieldValue();
        this.adaptee.y = toTwipFloor(value - (this.adaptee.scaleY * this.adaptee.textOffsetY));
    };
    AVM1TextField.prototype.getAutoSize = function () {
        return this.adaptee.autoSize;
    };
    AVM1TextField.prototype.setAutoSize = function (value) {
        // AVM1 treats |true| as "LEFT" and |false| as "NONE".
        if (value === true) {
            value = 'left';
        }
        else if (value === false) {
            value = 'none';
        }
        value = alCoerceString(this.context, value);
        this.adaptee.autoSize = value;
    };
    AVM1TextField.prototype.getBackground = function () {
        return this.adaptee.background;
    };
    AVM1TextField.prototype.setBackground = function (value) {
        value = alToBoolean(this.context, value);
        this.adaptee.background = value;
    };
    AVM1TextField.prototype.getBackgroundColor = function () {
        return this.adaptee.backgroundColor;
    };
    AVM1TextField.prototype.setBackgroundColor = function (value) {
        value = alToInt32(this.context, value);
        this.adaptee.backgroundColor = value;
    };
    AVM1TextField.prototype.getBorder = function () {
        return this.adaptee.border;
    };
    AVM1TextField.prototype.setBorder = function (value) {
        value = alToBoolean(this.context, value);
        this.adaptee.border = value;
    };
    AVM1TextField.prototype.getBorderColor = function () {
        return this.adaptee.borderColor;
    };
    AVM1TextField.prototype.setBorderColor = function (value) {
        value = alToInt32(this.context, value);
        this.adaptee.borderColor = value;
    };
    AVM1TextField.prototype.getBottomScroll = function () {
        return this.adaptee.bottomScrollV + 1;
    };
    AVM1TextField.prototype.getCondenseWhite = function () {
        notImplemented('AVM1Textfield.getCondenseWhite');
        return this.adaptee.condenseWhite;
    };
    AVM1TextField.prototype.setCondenseWhite = function (value) {
        notImplemented('AVM1Textfield.setCondenseWhite');
        value = alToBoolean(this.context, value);
        this.adaptee.condenseWhite = value;
    };
    AVM1TextField.prototype.getEmbedFonts = function () {
        notImplemented('AVM1Textfield.getEmbedFonts');
        return this.adaptee.embedFonts;
    };
    AVM1TextField.prototype.setEmbedFonts = function (value) {
        notImplemented('AVM1Textfield.setEmbedFonts');
        value = alToBoolean(this.context, value);
        this.adaptee.embedFonts = value;
    };
    AVM1TextField.prototype.getNewTextFormat = function () {
        return AVM1TextFormat.createFromNative(this.context, this.adaptee.textFormat);
    };
    AVM1TextField.prototype.getTextFormat = function (beginIndex, endIndex) {
        if (beginIndex === void 0) { beginIndex = -1; }
        if (endIndex === void 0) { endIndex = -1; }
        beginIndex = alToInteger(this.context, beginIndex);
        endIndex = alToInteger(this.context, endIndex);
        var as3TextFormat = this.adaptee.getTextFormat(beginIndex, endIndex);
        return AVM1TextFormat.createFromNative(this.context, as3TextFormat);
    };
    AVM1TextField.prototype.getGridFitType = function () {
        notImplemented('AVM1Textfield.getGridFitType');
        return ''; // this.adaptee.gridFitType;
    };
    AVM1TextField.prototype.setGridFitType = function (value) {
        notImplemented('AVM1Textfield.setGridFitType');
        //value = alCoerceString(this.context, value);
        //this.adaptee.gridFitType = value;
    };
    AVM1TextField.prototype.getHscroll = function () {
        return this.adaptee.scrollH + 1;
    };
    AVM1TextField.prototype.setHscroll = function (value) {
        value = alCoerceNumber(this.context, value);
        if (value <= 0)
            value = 1;
        this.adaptee.scrollH = value - 1;
    };
    AVM1TextField.prototype.getHtml = function () {
        return this.adaptee.html;
    };
    AVM1TextField.prototype.setHtml = function (value) {
        this.adaptee.html = !!value;
        // Flash doesn't update the displayed text at this point, but the return
        // value of `TextField#htmlText` is as though `TextField#htmlText = TextField#text` had
        // also been called. For now, we ignore that.
    };
    AVM1TextField.prototype.getHtmlText = function () {
        return this.adaptee.html ? this.adaptee.htmlText : this.adaptee.text;
    };
    AVM1TextField.prototype.setHtmlText = function (value) {
        // alToString turns `undefined` into an empty string, but we really do want "undefined" here.
        value = value === undefined ? 'undefined' : alToString(this.context, value);
        if (this.adaptee.html) {
            this.adaptee.htmlText = value;
        }
        else {
            this.adaptee.text = value;
        }
        this.updateVarFromText();
        this.syncTextFieldValue();
    };
    AVM1TextField.prototype.getLength = function () {
        return this.adaptee.length;
    };
    AVM1TextField.prototype.getMaxChars = function () {
        return this.adaptee.maxChars == 0 ? null : this.adaptee.maxChars;
    };
    AVM1TextField.prototype.setMaxChars = function (value) {
        value = alCoerceNumber(this.context, value);
        this.adaptee.maxChars = value;
    };
    AVM1TextField.prototype.getRestrict = function () {
        return this.adaptee.restrict;
    };
    AVM1TextField.prototype.setRestrict = function (value) {
        value = alCoerceString(this.context, value);
        this.adaptee.restrict = value;
    };
    AVM1TextField.prototype.getMaxhscroll = function () {
        return this.adaptee.maxScrollH + 1;
    };
    AVM1TextField.prototype.getMaxscroll = function () {
        return this.adaptee.maxScrollV + 1;
    };
    AVM1TextField.prototype.getMultiline = function () {
        return this.adaptee.multiline;
    };
    AVM1TextField.prototype.setMultiline = function (value) {
        value = alToBoolean(this.context, value);
        this.adaptee.multiline = value;
    };
    AVM1TextField.prototype.getPassword = function () {
        notImplemented('AVM1Textfield.getPassword');
        return this.adaptee.displayAsPassword;
    };
    AVM1TextField.prototype.setPassword = function (value) {
        notImplemented('AVM1Textfield.setPassword');
        value = alToBoolean(this.context, value);
        this.adaptee.displayAsPassword = value;
    };
    AVM1TextField.prototype.getScroll = function () {
        return this.adaptee.scrollV + 1;
    };
    AVM1TextField.prototype.setScroll = function (value) {
        value = alCoerceNumber(this.context, value);
        if (value <= 0)
            value = 1;
        this.adaptee.scrollV = value - 1;
    };
    AVM1TextField.prototype.getSelectable = function () {
        return this.adaptee.selectable;
    };
    AVM1TextField.prototype.setSelectable = function (value) {
        if (value == 'false') {
            value = false;
        }
        value = alToBoolean(this.context, value);
        if (!value && this.context.globals.Stage.avmStage.mouseManager.getFocus() == this.adaptee) {
            this.context.globals.Stage.avmStage.mouseManager.setFocus(null);
        }
        this.adaptee.selectable = value;
    };
    AVM1TextField.prototype.replaceSel = function (value) {
        value = alToString(this.context, value);
        this.adaptee.replaceSelectedText(value);
    };
    /**
     * for html text this will get ignored
     */
    AVM1TextField.prototype.setNewTextFormat = function (value) {
        var away3TextFormat;
        if (value instanceof AVM1TextFormat) {
            away3TextFormat = value.adaptee;
        }
        else {
            console.log('AVM1Textfield.setNewtextFormat - trying to set something other than a TextFormat', value);
            return;
        }
        if (!this.adaptee.textFormat) {
            this.adaptee.textFormat = new TextFormat();
        }
        //away3TextFormat.font_table=this.adaptee.textFormat.font_table;
        this.adaptee.newTextFormat = away3TextFormat;
    };
    /**
     * This should only have effect on text that is currently existent on the Textfield.
     * for new text we must use setNewTextFormat
     */
    AVM1TextField.prototype.setTextFormat = function () {
        if (this._variable) {
            this.syncTextFieldValue();
        }
        var beginIndex = -1, endIndex = -1, tf;
        switch (arguments.length) {
            case 0:
                return; // invalid amount of arguments
            case 1:
                tf = arguments[0];
                break;
            case 2:
                beginIndex = alToNumber(this.context, arguments[0]);
                tf = arguments[1];
                break;
            default:
                beginIndex = alToNumber(this.context, arguments[0]);
                endIndex = alToNumber(this.context, arguments[1]);
                tf = arguments[2];
                break;
        }
        //console.log(arguments);
        if (beginIndex >= 0 && endIndex == -1) {
            endIndex = beginIndex;
        }
        var as3TextFormat;
        if (tf instanceof AVM1TextFormat) {
            as3TextFormat = tf.adaptee;
            this.adaptee.setTextFormat(as3TextFormat, beginIndex, endIndex);
        }
    };
    AVM1TextField.prototype.getText = function () {
        if (this._variable) {
            this.syncTextFieldValue();
        }
        return this.adaptee.text;
    };
    AVM1TextField.prototype.setText = function (value) {
        // alToString turns `undefined` into an empty string, but we really do want "undefined" here.
        value = value === undefined ? '' : alToString(this.context, value);
        var avm1ContextUtils = this.context.utils;
        if (this.adaptee.parent) {
            var clip = getAVM1Object(this.adaptee.parent, this.context);
            avm1ContextUtils.setProperty(clip, this._variable, value);
        }
        this.adaptee.text = value;
        if (this._variable) {
            this.updateVarFromText();
            this.syncTextFieldValue();
        }
    };
    AVM1TextField.prototype.getTextColor = function () {
        return this.adaptee.textColor;
    };
    AVM1TextField.prototype.setTextColor = function (value) {
        value = alToInt32(this.context, value);
        this.adaptee.textColor = value;
    };
    AVM1TextField.prototype.getTextHeight = function () {
        if (this._variable) {
            this.syncTextFieldValue();
        }
        return this.adaptee.textHeight;
    };
    AVM1TextField.prototype.setTextHeight = function (value) {
        notImplemented('AVM1TextField.setTextHeight');
    };
    AVM1TextField.prototype.getTextWidth = function () {
        if (this._variable) {
            this.syncTextFieldValue();
        }
        return this.adaptee.textWidth;
    };
    AVM1TextField.prototype.setTextWidth = function (value) {
        notImplemented('AVM1TextField.setTextWidth');
    };
    AVM1TextField.prototype.getType = function () {
        return this.adaptee.type;
    };
    AVM1TextField.prototype.setType = function (value) {
        value = alCoerceString(this.context, value);
        if (value)
            value = value.toLowerCase();
        this.adaptee.type = value;
    };
    AVM1TextField.prototype.getVariable = function () {
        return this._variable;
    };
    AVM1TextField.prototype.setVariable = function (name) {
        name = alCoerceString(this.context, name);
        if (name === this._variable) {
            return;
        }
        var instance = this.adaptee;
        AVM1TextField.allTextfields[instance.id] = this;
        this._prevTextVarContent = this.adaptee.text;
        this._syncTextFieldValue(instance, name);
        if (this._exitFrameHandler && !name) {
            instance.removeEventListener('exitFrame', this._exitFrameHandler); //80pro: should be exitFrame
            this._exitFrameHandler = null;
        }
        this._variable = name;
        if (!this._exitFrameHandler && name) {
            this._exitFrameHandler = this._onAS3ObjectExitFrame.bind(this);
            instance.addEventListener('exitFrame', this._exitFrameHandler); //80pro: should be exitFrame
        }
    };
    AVM1TextField.prototype._onAS3ObjectExitFrame = function () {
        if (this._variable)
            this._syncTextFieldValue(this.adaptee, this._variable);
    };
    AVM1TextField.prototype.syncTextFieldValue = function () {
        this._syncTextFieldValue(this.adaptee, this._variable);
    };
    AVM1TextField.prototype.updateVarFromText = function () {
        if (this._textVarHolder) {
            this.context.utils.setProperty(this._textVarHolder, this._textVarPropName, this.adaptee.html ?
                this.adaptee.htmlText : this.adaptee.text);
            this._prevTextVarContent = this.adaptee.text;
        }
    };
    AVM1TextField.prototype._syncTextFieldValue = function (instance, name) {
        if (!name || name == '') {
            return;
        }
        var avm1ContextUtils = this.context.utils;
        if (this._textVarHolder == null) {
            // todo: this could be probably done by using this.context.resolveTarget
            var hasPath = name.indexOf('.') >= 0 || name.indexOf(':') >= 0;
            if (hasPath) {
                var targetPath = name.split(/[.:\/]/g);
                this._textVarPropName = targetPath.pop();
                if (targetPath[0] == '_root'
                    || targetPath[0] == '_level0'
                    || targetPath[0] == '_level'
                    || targetPath[0] === '') {
                    var parent_1 = instance.parent;
                    this._textVarHolder = null;
                    while (parent_1) {
                        if (parent_1.isAVMScene) {
                            this._textVarHolder = parent_1.adapter;
                            parent_1 = null;
                        }
                        else {
                            parent_1 = parent_1.parent;
                        }
                    }
                    if (this._textVarHolder === null) {
                        return; // text field is not part of the stage yet
                    }
                    targetPath.shift();
                    if (targetPath[0] === '') {
                        targetPath.shift();
                    }
                }
                else if (targetPath[0] == '_global') {
                    targetPath.shift();
                    this._textVarHolder = this.context.globals;
                }
                else {
                    if (!instance.parent) {
                        return;
                    }
                    this._textVarHolder = getAVM1Object(instance.parent, this.context);
                }
                var childName = null;
                while (targetPath.length > 0) {
                    childName = targetPath.shift();
                    this._textVarHolder = avm1ContextUtils.getProperty(this._textVarHolder, childName);
                    if (!this._textVarHolder) {
                        return; // cannot find child clip
                    }
                }
            }
            else {
                this._textVarPropName = name;
                if (instance.parent && instance.parent.parent) {
                    this._textVarHolder = getAVM1Object(instance.parent, this.context);
                    // in cases where another object exists at the same path, and also has a textvar set,
                    // we want to set the already existing path as "textVarHolder",
                    // so that the text-var-property descripten is shared, and both textields update in sync
                    var child = void 0;
                    var parentParent = instance.parent.parent;
                    var len = parentParent.numChildren;
                    for (var i = 0; i < len; i++) {
                        child = parentParent.getChildAt(i);
                        if (child != instance.parent && child.name == instance.parent.name) {
                            if (avm1ContextUtils.hasProperty(child.adapter, this._textVarPropName)) {
                                this._textVarHolder = child.adapter;
                            }
                        }
                    }
                }
            }
        }
        if (!this._textVarHolder) {
            // the object that holds the variable could not be found yet
            // we add it to a queue that will be worked off before any script executes
            AVM1TextField.textFieldVars.push(this);
            if (instance.html) {
                return this._prevTextVarContent = instance.htmlText;
            }
            this._prevTextVarContent = instance.text;
            return;
        }
        // if the variable does not exist, fill it from textfield-content
        if (!avm1ContextUtils.hasProperty(this._textVarHolder, this._textVarPropName)) {
            // the textvar does not exists yet. we create it and fill it with text-content
            var v = void 0;
            if (instance.html) {
                v = {
                    isTextVar: true,
                    value: undefined
                };
                if (instance.htmlText != '') {
                    v.value = instance.htmlText;
                }
                avm1ContextUtils.setProperty(this._textVarHolder, this._textVarPropName, v);
                avm1ContextUtils.setProperty(this._textVarHolder, this._textVarPropName + '_internal_TF', this);
                this._prevTextVarContent = instance.htmlText;
                return;
            }
            v = {
                isTextVar: true,
                value: undefined
            };
            if (instance.text != '') {
                v.value = instance.text;
            }
            avm1ContextUtils.setProperty(this._textVarHolder, this._textVarPropName, v);
            avm1ContextUtils.setProperty(this._textVarHolder, this._textVarPropName + '_internal_TF', this);
            this._prevTextVarContent = instance.text;
            return;
        }
        //  if no "_internal_TF" property exists next to the variable, we create it, and set its value to "this"
        //  this is used to find textfields to focus, when setFocus is called on a textfield-variable
        if (!avm1ContextUtils.hasProperty(this._textVarHolder, this._textVarPropName + '_internal_TF')) {
            avm1ContextUtils.setProperty(this._textVarHolder, this._textVarPropName + '_internal_TF', this);
        }
        // get the value of the text-variable
        var newTextVarContent = avm1ContextUtils.getProperty(this._textVarHolder, this._textVarPropName);
        if (typeof newTextVarContent !== 'string') {
            newTextVarContent = alToString(this.context, newTextVarContent);
        }
        if (instance.html) {
            // the value of the text-variable has changed
            if (newTextVarContent !== this._prevTextVarContent) {
                // textvar has changed. update text from var
                instance.htmlText = (typeof newTextVarContent === 'undefined') ? '' : newTextVarContent;
                this._prevTextVarContent = newTextVarContent;
                return;
            }
            if (instance.htmlText !== newTextVarContent
                && (!(typeof newTextVarContent === 'undefined' && instance.htmlText === ''))) {
                // makes sure text is set correctly in case the timeline has interferred.
                instance.htmlText = (typeof newTextVarContent === 'undefined') ? '' : newTextVarContent;
            }
        }
        else {
            if (newTextVarContent !== this._prevTextVarContent) {
                // textvar has changed. update text from var
                instance.text = (typeof newTextVarContent === 'undefined') ? '' : newTextVarContent;
                this._prevTextVarContent = newTextVarContent;
                return;
            }
            if (instance.text !== newTextVarContent
                && (!(typeof newTextVarContent === 'undefined' && instance.text === ''))) {
                // makes sure text is set correctly in case the timeline has interferred.
                instance.text = (typeof newTextVarContent === 'undefined') ? '' : newTextVarContent;
            }
        }
    };
    AVM1TextField.prototype.getWordWrap = function () {
        return this.adaptee.wordWrap;
    };
    AVM1TextField.prototype.setWordWrap = function (value) {
        value = alToBoolean(this.context, value);
        this.adaptee.wordWrap = value;
    };
    AVM1TextField.prototype._initEventsHandlers = function () {
        this.bindEvents(EventsListForMC);
    };
    AVM1TextField.allTextfields = {};
    AVM1TextField.textFieldVars = [];
    return AVM1TextField;
}(AVM1SymbolBase));
export { AVM1TextField };
